const PRODUCT_ALIASES = [
  { prefix: '@ProductImages/', storagePrefix: 'images/' },
  { prefix: '@ProductDocs/', storagePrefix: 'docs/' },
  { prefix: '@ProductUploads/', storagePrefix: '' },
];

const MEDIA_BASE_URL = String(process.env.MEDIA_BASE_URL || '').trim().replace(/\/+$/, '');

const resolveRuntimeMediaBaseUrl = () => {
  if (!MEDIA_BASE_URL) return '';
  if (typeof window === 'undefined') return MEDIA_BASE_URL;
  if (!/^https?:\/\//i.test(MEDIA_BASE_URL)) return MEDIA_BASE_URL;

  try {
    const url = new URL(MEDIA_BASE_URL);
    if (String(window.location?.protocol || '').toLowerCase() === 'https:' && url.protocol === 'http:') {
      url.protocol = 'https:';
    }
    return String(url).replace(/\/+$/, '');
  } catch {
    return MEDIA_BASE_URL;
  }
};

const normalizeLeadingSlashes = (value) => String(value ?? '').replace(/^\/+/, '');

const isPublicStoragePath = (path) => {
  const normalizedPath = normalizeLeadingSlashes(path);
  return normalizedPath.startsWith('public/')
    || normalizedPath.startsWith('site/')
    || normalizedPath.startsWith('images/')
    || normalizedPath.startsWith('docs/');
};

const buildPrivateFileUrl = (path) => {
  const normalizedPath = normalizeLeadingSlashes(path).replace(/^(files\/)+/, '');
  if (!normalizedPath) return '';
  return `/private-files/${normalizedPath.split('/').map(encodeURIComponent).join('/')}`;
};

const buildMediaUrl = (path) => {
  const normalizedPath = String(path ?? '').replace(/^\/+/, '');
  const sameOriginPath = `/${normalizedPath}`;
  const mediaBaseUrl = resolveRuntimeMediaBaseUrl();
  if (!mediaBaseUrl) return sameOriginPath;

  const relativePath = normalizedPath.startsWith('files/')
    ? normalizedPath.slice('files/'.length)
    : normalizedPath;

  return `${mediaBaseUrl}/${relativePath}`.replace(/([^:]\/)\/+/g, '$1');
};

function normalizeProductAliasPath(src) {
  const raw = String(src ?? '').trim();
  if (!raw) return raw;

  for (const entry of PRODUCT_ALIASES) {
    if (raw.startsWith(entry.prefix)) {
      const tail = normalizeLeadingSlashes(raw.slice(entry.prefix.length));
      return `/files/${entry.storagePrefix}${tail}`.replace(/\/{2,}/g, '/');
    }
  }

  return raw;
}

/**
 * Normaliza src de arquivo público para URL no mesmo host: `/files/...`.
 * Também aceita aliases semânticos:
 * - `@ProductImages/...`
 * - `@ProductDocs/...`
 * - `@ProductUploads/...`
 * E também aceita a key canônica persistida no banco:
 * - `tenant-or-public/.../arquivo.ext`
 *
 * @param {string} [src]
 * @returns {string}
 */
export function resolveFilesPublicPath(src) {
  const raw = normalizeProductAliasPath(src);
  if (!raw) return raw;

  if (/^https?:\/\//i.test(raw)) {
    try {
      const { pathname, search, hash } = new URL(raw);
      const idx = pathname.indexOf('/files/');
      const base = idx >= 0 ? pathname.slice(idx) : pathname;
      return `${base}${search}${hash}`;
    } catch {
      return raw;
    }
  }

  let path = raw.replace(/^\/+/, '');
  path = path.replace(/^(files\/)+/, 'files/');
  if (path.startsWith('files/')) {
    const storagePath = path.slice('files/'.length);
    return isPublicStoragePath(storagePath) ? buildMediaUrl(path) : buildPrivateFileUrl(storagePath);
  }

  if (path.startsWith('public/')) return buildMediaUrl(`files/${path}`);
  return isPublicStoragePath(path) ? buildMediaUrl(`files/${path}`) : buildPrivateFileUrl(path);
}

/**
 * Acrescenta uma versão de conteúdo à URL pública. Arquivos editoriais podem
 * manter um nome estável no storage, mas o navegador precisa de uma URL nova
 * quando o conteúdo for sobrescrito.
 *
 * @param {string} src Caminho persistido ou URL pública.
 * @param {string|number|Date} version Versão ligada à atualização do conteúdo.
 * @returns {string}
 */
export function resolveVersionedFilesPublicPath(src, version) {
  const resolved = resolveFilesPublicPath(src);
  const token = version instanceof Date ? version.toISOString() : String(version ?? '').trim();
  if (!resolved || !token) return resolved;

  try {
    const absolute = /^https?:\/\//i.test(resolved);
    const url = new URL(resolved, 'http://nucleop-files.local');
    url.searchParams.set('v', token);
    return absolute ? url.toString() : `${url.pathname}${url.search}${url.hash}`;
  } catch {
    const [withoutHash, hash = ''] = resolved.split('#', 2);
    const separator = withoutHash.includes('?') ? '&' : '?';
    return `${withoutHash}${separator}v=${encodeURIComponent(token)}${hash ? `#${hash}` : ''}`;
  }
}

export function resolveProductImagesPublicPath(src) {
  return resolveFilesPublicPath(`@ProductImages/${normalizeLeadingSlashes(src)}`);
}

export function resolveProductDocsPublicPath(src) {
  return resolveFilesPublicPath(`@ProductDocs/${normalizeLeadingSlashes(src)}`);
}
